import { useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  Typography,
  SelectChangeEvent,
  Collapse,
  Chip,
} from "@mui/material";
import {
  Delete as Trash2,
  Settings as Settings2,
  Close as X,
} from "@mui/icons-material";
import { DatePicker, DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import type { CriterionNode } from "./nested-dsl-utils";
import FieldSelectionDrawer from "./FieldSelectionDrawer";

const datePickerFormats = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY", "MM/YYYY"];
const dateTimePickerFormats = ["DD/MM/YYYY HH:mm", "MM/DD/YYYY HH:mm"];
const timePickerFormats = ["HH:mm", "HH:mm:ss"];
const luxonFormats: Record<string, string> = {
  "DD/MM/YYYY": "dd/MM/yyyy",
  "MM/DD/YYYY": "MM/dd/yyyy",
  "DD/MM/YYYY HH:mm": "dd/MM/yyyy HH:mm",
  "MM/DD/YYYY HH:mm": "MM/dd/yyyy HH:mm",
  "HH:mm": "HH:mm",
  "HH:mm:ss": "HH:mm:ss",
  YYYY: "yyyy",
  "MM/YYYY": "MM/yyyy",
};

interface CriteriaRowProps {
  criterion: CriterionNode;
  studyId?: string;
  onChange: (updated: CriterionNode) => void;
  onDelete: () => void;
  dragHandleProps?: any;
  platformType?: "registry" | "study";
}

// OG operators list
// const OPERATORS = [
//   { value: "=", label: "equals" },
//   { value: "!=", label: "not equals" },
//   { value: ">", label: "greater than" },
//   { value: ">=", label: "greater than or equal" },
//   { value: "<", label: "less than" },
//   { value: "<=", label: "less than or equal" },
//   { value: "between", label: "between" },
//   { value: "in", label: "in list" },
//   { value: "not_in", label: "not in list" },
//   { value: "is_null", label: "is null" },
//   { value: "is_not_null", label: "is not null" },
//   { value: "contains", label: "contains (text)" },
// ];

const textConditions = [
  { value: "contains", label: "contains (text)" },
  { value: "is_null", label: "is null" },
  { value: "is_not_null", label: "is not null" },
];

const choiceConditions = [
  { value: "=", label: "is" },
  { value: "contains", label: "contains (text)" },
  // { value: "in", label: "in list" },
  // { value: "not_in", label: "not in list" },
  { value: "is_null", label: "is null" },
  { value: "is_not_null", label: "is not null" },
];

const checkboxConditions = [
  { value: "=", label: "checked" },
  { value: "contains", label: "contains (text)" },
  // { value: "in", label: "in list" },
  // { value: "not_in", label: "not in list" },
  { value: "is_null", label: "is null" },
  { value: "is_not_null", label: "is not null" },
];

const numberConditions = [
  { value: "=", label: "equals" },
  { value: "!=", label: "not equals" },
  { value: ">", label: "greater than" },
  { value: ">=", label: "greater than or equal" },
  { value: "<", label: "less than" },
  { value: "<=", label: "less than or equal" },
  { value: "between", label: "between" },
  { value: "is_null", label: "is null" },
  { value: "is_not_null", label: "is not null" },
];

const tagsConditions = [
  { value: "contains", label: "has any of" },
  { value: "not_contains", label: "does not have any of" },
  { value: "is_null", label: "has no tags" },
  { value: "is_not_null", label: "has tags" },
];

const conditionOptionMap: Record<string, any[]> = {
  text: textConditions,
  date: numberConditions,
  number: numberConditions,
  slider: numberConditions,
  calculated_field: numberConditions,
  calculated_field_text: textConditions,
  checkbox: checkboxConditions,
  dropdown: choiceConditions,
  radio: choiceConditions,
  tags: tagsConditions,
};

// Operators that don't require a value input
const NO_VALUE_OPERATORS = ["is_null", "is_not_null"];

const CHOICE_QUESTION_TYPES = ["checkbox", "radio", "dropdown"];
const NUMBER_QUESTION_TYPES = ["number", "slider", "calculated_field"];
const TEXT_QUESTION_TYPES = ["text", "calculated_field_text"];

/**
 * Shape of the value control for a given operator + question type.
 * Used to clear criterion.value when switching operators would swap
 * control kinds (e.g. choice id dropdown → free-text contains).
 */
type CriterionValueMode =
  | "none"
  | "choice"
  | "tags"
  | "text"
  | "number"
  | "number_between"
  | "date"
  | "date_between"
  | "empty";

const getValueMode = (
  cmp: string,
  questionType: string = "",
): CriterionValueMode => {
  if (NO_VALUE_OPERATORS.includes(cmp) || !cmp) {
    return "none";
  }

  if (questionType === "tags") {
    if (cmp === "contains" || cmp === "not_contains") {
      return "tags";
    }
    return "none";
  }

  if (CHOICE_QUESTION_TYPES.includes(questionType)) {
    if (cmp === "=") return "choice";
    if (cmp === "contains") return "text";
    return "empty";
  }

  if (TEXT_QUESTION_TYPES.includes(questionType)) {
    return "text";
  }

  if (NUMBER_QUESTION_TYPES.includes(questionType)) {
    return cmp === "between" ? "number_between" : "number";
  }

  if (questionType === "date") {
    return cmp === "between" ? "date_between" : "date";
  }

  return "empty";
};

// Helper function to render date picker components
const renderDatePicker = (
  value: any,
  onChange: (newValue: any) => void,
  format: string = "DD/MM/YYYY",
  placeholder: string = "Select date",
) => {
  const luxonFormat = luxonFormats[format] || "dd/MM/yyyy";

  if (dateTimePickerFormats.includes(format)) {
    return (
      <DateTimePicker
        format={luxonFormat}
        value={value ? DateTime.fromFormat(value, luxonFormat) : null}
        timeSteps={{
          minutes: 1,
          seconds: 1,
        }}
        onChange={(newValue: any) => {
          onChange(newValue ? newValue.toFormat(luxonFormat) : null);
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            inputProps: {
              readOnly: true,
              placeholder,
            },
            sx: {
              width: "100%",
            },
          },
        }}
      />
    );
  }

  if (timePickerFormats.includes(format)) {
    return (
      <TimePicker
        views={
          format === "HH:mm:ss"
            ? ["hours", "minutes", "seconds"]
            : ["hours", "minutes"]
        }
        format={luxonFormat}
        value={value ? DateTime.fromFormat(value, luxonFormat) : null}
        onChange={(newValue: any) => {
          onChange(newValue ? newValue.toFormat(luxonFormat) : null);
        }}
        timeSteps={{
          minutes: 1,
          seconds: 1,
        }}
        slotProps={{
          textField: {
            sx: {
              width: "100%",
            },
            inputProps: {
              readOnly: true,
              placeholder,
            },
          },
        }}
      />
    );
  }

  if (datePickerFormats.includes(format)) {
    return (
      <DatePicker
        views={
          format === "YYYY"
            ? ["year"]
            : format === "MM/YYYY"
              ? ["month", "year"]
              : undefined
        }
        format={luxonFormat}
        value={value ? DateTime.fromFormat(value, luxonFormat) : null}
        onChange={(newValue: any) => {
          onChange(newValue ? newValue.toFormat(luxonFormat) : null);
        }}
        slotProps={{
          textField: {
            sx: {
              width: "100%",
            },
            inputProps: {
              readOnly: true,
              placeholder,
            },
          },
        }}
      />
    );
  }

  return null;
};

export function CriteriaRow({
  criterion,
  studyId,
  onChange,
  onDelete,
  platformType,
}: // dragHandleProps,
CriteriaRowProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Update criterion with field metadata when fact changes
  const handleFieldChange = (selection: {
    varname: string;
    label: string;
    questionType: string;
    questionId: string;
    studyName: string;
    formName: string;
    studyId: string;
    questionTypeLabel: string;
    choices?: { id: string; label: string }[];
    source: "study" | "profile" | "registry";
    format?: string;
    responseType?: string;
    isCanonical?: boolean;
  }) => {
    if (
      selection.questionId === criterion.questionId &&
      selection.source === criterion.source &&
      !!selection.isCanonical === !!criterion.isCanonical
    ) {
      return;
    }
    // const field = availableFields.find((f) => f.value === value);
    const updates: Partial<CriterionNode> = {};
    updates.fact = selection.varname;

    if (selection.questionId) {
      updates.questionId = selection.questionId;
    }

    if (selection?.questionType) {
      updates.where = {
        datatype: selection.questionType,
        ...(selection.format ? { format: selection.format } : {}),
      };
    }

    updates.source = selection.source;
    updates.isCanonical = !!selection.isCanonical;
    updates.cmp = ""; // Reset operator on field change
    updates.value = undefined;

    updates.factDetails = {
      label: selection.label,
      varname: selection.varname,
      questionType: selection.questionType,
      formName: selection.formName,
      studyName: selection.studyName,
      studyId: selection.studyId,
      questionTypeLabel: selection.questionTypeLabel,
      choices: selection.choices,
      format: selection.format,
      responseType: selection.responseType,
    };

    onChange({ ...criterion, ...updates });
    setDrawerOpen(false);
  };

  // Determine if value input should be shown
  const requiresValue = !NO_VALUE_OPERATORS.includes(criterion.cmp);

  // Render value input based on operator and datatype
  const renderValueInput = () => {
    if (!requiresValue) return null;

    // const datatype = criterion.where?.datatype || "text";
    const inputType =
      ["text", "calculated_field_text"].includes(
        criterion?.factDetails?.questionType || "",
      ) ||
      (criterion.cmp === "contains" &&
        ["checkbox", "radio", "dropdown"].includes(
          criterion?.factDetails?.questionType || "",
        ))
        ? "text"
        : ["number", "slider", "calculated_field"].includes(
              criterion?.factDetails?.questionType || "",
            )
          ? "number"
          : ["date"].includes(criterion?.factDetails?.questionType || "")
            ? "date"
            : "";
    // Between operator requires two inputs
    if (criterion.cmp === "between" && inputType !== "date") {
      const values = Array.isArray(criterion.value)
        ? criterion.value
        : [null, null];
      return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1 }}>
          <TextField
            type={inputType === "number" ? "number" : "text"}
            value={values[0] || ""}
            onChange={(e) => {
              const newValues = [
                inputType === "number"
                  ? parseFloat(e.target.value)
                  : e.target.value,
                values[1],
              ];
              onChange({ ...criterion, value: newValues });
            }}
            placeholder="Min"
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
            inputProps={{
              "data-testid": `input-criterion-value-min-${criterion.id}`,
            }}
          />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            and
          </Typography>
          <TextField
            type={inputType === "number" ? "number" : "text"}
            value={values[1] || ""}
            onChange={(e) => {
              const newValues = [
                values[0],
                inputType === "number"
                  ? parseFloat(e.target.value)
                  : e.target.value,
              ];
              onChange({ ...criterion, value: newValues });
            }}
            placeholder="Max"
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
            inputProps={{
              "data-testid": `input-criterion-value-max-${criterion.id}`,
            }}
          />
        </Box>
      );
    }

    if (
      criterion.cmp === "=" &&
      ["checkbox", "radio", "dropdown"].includes(
        criterion?.factDetails?.questionType || "",
      )
    ) {
      return (
        <FormControl size="small" sx={{ width: 180 }}>
          <Select
            value={criterion.value || ""}
            onChange={(e: SelectChangeEvent) =>
              onChange({ ...criterion, value: e.target.value })
            }
            sx={{ height: "48px" }}
            data-testid={`select-criterion-value-${criterion.id}`}
          >
            {criterion?.factDetails?.choices?.map((item) => {
              return <MenuItem value={item.id}>{item.label}</MenuItem>;
            })}
          </Select>
        </FormControl>
      );
    }

    if (
      criterion.cmp !== "contains" &&
      ["checkbox", "radio", "dropdown"].includes(
        criterion?.factDetails?.questionType || "",
      )
    ) {
      return null;
    }

    // IN/NOT_IN operators can accept array or comma-separated string
    // if (criterion.cmp === "in" || criterion.cmp === "not_in") {
    //   // Always store as string to preserve user input including trailing commas
    //   const displayValue =
    //     typeof criterion.value === "string"
    //       ? criterion.value
    //       : Array.isArray(criterion.value)
    //       ? criterion.value.join(", ")
    //       : "";

    //   return (
    //     <TextField
    //       value={displayValue}
    //       onChange={(e) => {
    //         // Store the raw string value - don't parse until it's used
    //         onChange({ ...criterion, value: e.target.value });
    //       }}
    //       placeholder="value1, value2, value3"
    //       size="small"
    //       sx={{ flex: 1, minWidth: 0 }}
    //       inputProps={{
    //         "data-testid": `input-criterion-value-${criterion.id}`,
    //       }}
    //     />
    //   );
    // }

    // Tags multi-select
    if (
      criterion?.factDetails?.questionType === "tags" &&
      (criterion.cmp === "contains" || criterion.cmp === "not_contains")
    ) {
      const selectedIds: string[] = Array.isArray(criterion.value)
        ? criterion.value
        : [];
      const choices = criterion?.factDetails?.choices || [];
      return (
        <Select
          multiple
          size="small"
          value={selectedIds}
          onChange={(e) =>
            onChange({ ...criterion, value: e.target.value as string[] })
          }
          sx={{ flex: 1, minWidth: 120 }}
          renderValue={(selected) =>
            (selected as string[])
              .map((id) => choices.find((c: any) => c.id === id)?.label || id)
              .join(", ")
          }
        >
          {choices.map((choice: any) => (
            <MenuItem key={choice.id} value={choice.id}>
              {choice.label}
            </MenuItem>
          ))}
        </Select>
      );
    }

    // Standard single value input

    if (inputType === "date") {
      // Get the date format from question properties or default to DD/MM/YYYY
      const format = criterion?.factDetails?.format || "DD/MM/YYYY";

      if (criterion.cmp === "between") {
        // Between operator requires two date inputs
        const values = Array.isArray(criterion.value)
          ? criterion.value
          : [null, null];

        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1 }}>
            <Box sx={{ flex: 1 }}>
              {renderDatePicker(
                values[0],
                (newValue) => {
                  const newValues = [newValue, values[1]];
                  onChange({ ...criterion, value: newValues });
                },
                format,
                "Start date",
              )}
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              and
            </Typography>
            <Box sx={{ flex: 1 }}>
              {renderDatePicker(
                values[1],
                (newValue) => {
                  const newValues = [values[0], newValue];
                  onChange({ ...criterion, value: newValues });
                },
                format,
                "End date",
              )}
            </Box>
          </Box>
        );
      } else {
        // Single date input for other operators
        return (
          <Box sx={{ flex: 1 }}>
            {renderDatePicker(
              criterion.value,
              (newValue) => {
                onChange({ ...criterion, value: newValue });
              },
              format,
              "Select date",
            )}
          </Box>
        );
      }
    }

    if (inputType === "") {
      return null;
    }

    return (
      <TextField
        type={inputType === "number" ? "number" : "text"}
        value={criterion.value || ""}
        onChange={(e) => {
          const value =
            inputType === "number"
              ? parseFloat(e.target.value)
              : e.target.value;
          onChange({ ...criterion, value });
        }}
        placeholder="Value"
        size="small"
        sx={{ flex: 1, minWidth: 0 }}
        inputProps={{ "data-testid": `input-criterion-value-${criterion.id}` }}
      />
    );
  };

  const changeOperator = (newOp: string) => {
    const questionType = criterion?.factDetails?.questionType || "";
    const oldMode = getValueMode(criterion.cmp, questionType);
    const newMode = getValueMode(newOp, questionType);
    onChange({
      ...criterion,
      cmp: newOp,
      ...(oldMode !== newMode ? { value: undefined } : {}),
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "flex-start",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        backgroundColor: "background.paper",
        "&:hover": {
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      {/* Drag Handle */}
      {/* <Box
        {...dragHandleProps}
        sx={{
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
          color: "text.secondary",
          "&:hover": { color: "text.primary" },
          mt: 1,
        }}
        data-testid={`handle-criterion-${criterion.id}`}
      >
        <GripVertical sx={{ fontSize: 20 }} />
      </Box> */}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          minWidth: 0,
        }}
      >
        {/* Main Row: Field, Operator, Value */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          {/* Field Selector */}
          <Box
            onClick={() => setDrawerOpen(true)}
            sx={{
              flex: 1,
              width: 200,
              height: "48px",
              px: 1.5,
              py: 0.75,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": {
                borderColor: "text.primary",
              },
            }}
            data-testid={`select-criterion-field-${criterion.id}`}
          >
            {criterion.factDetails?.varname ? (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {criterion.isCanonical && criterion.source === "study"
                    ? `Mapped - ${criterion.factDetails?.varname}`
                    : criterion.source === "study"
                    ? `${criterion?.factDetails?.studyName} - ${criterion?.factDetails?.formName} - ${criterion.factDetails?.varname}`
                    : criterion.source === "registry"
                      ? `Registry - ${criterion?.factDetails?.formName} - ${criterion.factDetails?.varname}`
                      : criterion.factDetails?.varname}
                </Typography>
                <Chip
                  label={criterion?.factDetails?.questionTypeLabel}
                  sx={{ ml: 1 }}
                  variant={"outlined"}
                  size="small"
                />
              </>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  color: criterion.questionId
                    ? "text.primary"
                    : "text.secondary",
                }}
              >
                Select field...
              </Typography>
            )}
          </Box>

          {/* Operator Selector */}
          <FormControl size="small" sx={{ width: 180 }}>
            <Select
              value={criterion.cmp}
              onChange={(e: SelectChangeEvent) =>
                changeOperator(e.target.value)
              }
              sx={{ height: "48px" }}
              data-testid={`select-criterion-operator-${criterion.id}`}
            >
              {conditionOptionMap?.[
                criterion?.factDetails?.questionType || ""
              ]?.map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Value Input */}
          {requiresValue && (
            <Box sx={{ flex: 1, minWidth: 150 }}>{renderValueInput()}</Box>
          )}
        </Box>

        {/* Settings Row (Tolerance, Time Window, NOT) */}
        <Collapse in={settingsOpen}>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              flexWrap: "wrap",
              p: 1.5,
              backgroundColor: "action.hover",
              borderRadius: 1,
            }}
          >
            {/* Tolerance */}
            {criterion?.factDetails?.questionType === "number" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                  Tolerance :
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  inputProps={{
                    min: "0",
                    max: "100",
                    step: "1",
                    "data-testid": `input-criterion-tolerance-${criterion.id}`,
                  }}
                  value={criterion.tolerance || ""}
                  onChange={(e) => {
                    const value = e.target.value
                      ? parseFloat(e.target.value)
                      : undefined;
                    onChange({ ...criterion, tolerance: value });
                  }}
                  placeholder="0"
                  sx={{ width: 80 }}
                />
              </Box>
            )}

            {/* Critical Flag */}
            {criterion.tolerance !== undefined && criterion.tolerance > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={criterion.critical || false}
                    onChange={(e) => {
                      onChange({ ...criterion, critical: e.target.checked });
                    }}
                    size="small"
                    inputProps={
                      {
                        "data-testid": `checkbox-criterion-critical-${criterion.id}`,
                      } as any
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    Strict (ignore tolerance)
                  </Typography>
                }
              />
            )}

            {/* Time Window */}
            {criterion.fact.startsWith("crf.") && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                  Within:
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  inputProps={{
                    min: "1",
                    "data-testid": `input-criterion-window-${criterion.id}`,
                  }}
                  value={
                    criterion.window?.days || criterion.window?.hours || ""
                  }
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value > 0) {
                      onChange({
                        ...criterion,
                        window: { days: value },
                      });
                    } else {
                      const { window, ...rest } = criterion;
                      onChange(rest as CriterionNode);
                    }
                  }}
                  placeholder="7"
                  sx={{ width: 64 }}
                />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  days
                </Typography>
              </Box>
            )}

            {/* Row-level NOT */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={criterion.negate || false}
                  onChange={(e) => {
                    onChange({ ...criterion, negate: e.target.checked });
                  }}
                  size="small"
                  inputProps={
                    {
                      "data-testid": `checkbox-criterion-not-${criterion.id}`,
                    } as any
                  }
                />
              }
              label={<Typography variant="body2">NOT</Typography>}
            />
          </Box>
        </Collapse>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
        {/* Settings Toggle */}
        <IconButton
          size="small"
          onClick={() => setSettingsOpen(!settingsOpen)}
          sx={{
            backgroundColor: settingsOpen ? "action.selected" : "transparent",
          }}
          data-testid={`button-criterion-settings-${criterion.id}`}
        >
          {settingsOpen ? (
            <X sx={{ fontSize: 20 }} />
          ) : (
            <Settings2 sx={{ fontSize: 20 }} />
          )}
        </IconButton>

        {/* Delete Button */}
        <IconButton
          size="small"
          onClick={onDelete}
          data-testid={`button-delete-criterion-${criterion.id}`}
        >
          <Trash2 sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Field Selection Drawer */}
      {drawerOpen && (
        <FieldSelectionDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          selectedField={criterion}
          onFieldChange={handleFieldChange}
          studyId={studyId}
          platformType={platformType}
        />
      )}
    </Box>
  );
}
